// /app/frontend/src/api/identity.js
/**
 * Identity Verification API Client
 * 
 * Handles Stripe Identity verification for Elite membership gating.
 * Users must verify their identity before purchasing Elite/Elite+ memberships.
 */

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/identity`;

/**
 * Get the current user's identity verification status
 * @param {string} token - Auth token
 * @returns {Promise<{identity: {status: string, verified_at: string|null, last_error: string|null}}>}
 */
export async function getIdentityStatus(token) {
  const res = await fetch(`${API}/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error('Failed to fetch identity status');
  }
  return res.json();
}

/**
 * Start a new identity verification session
 * Redirects user to Stripe-hosted verification flow
 * @param {string} token - Auth token
 * @returns {Promise<{url: string, session_id: string}>}
 */
export async function startIdentityVerification(token) {
  const res = await fetch(`${API}/start`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      return_url: `${window.location.origin}/shop?identity=return`,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to start identity verification');
  }
  return res.json();
}

/**
 * Manually refresh identity status from Stripe
 * Useful if webhook was missed or user wants to check
 * @param {string} token - Auth token
 * @returns {Promise<{identity: {status: string, verified_at: string|null, last_error: string|null}}>}
 */
export async function refreshIdentityStatus(token) {
  const res = await fetch(`${API}/refresh`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    throw new Error('Failed to refresh identity status');
  }
  return res.json();
}

/**
 * Check if user's identity is verified
 * @param {object} user - User object from context
 * @returns {boolean}
 */
export function isIdentityVerified(user) {
  if (!user) return false;
  
  // Check new identity object first
  const identity = user.identity || {};
  if (identity.status === 'verified') return true;
  
  // Fallback to legacy field
  if (user.identity_verification_status === 'verified') return true;
  
  return false;
}

/**
 * Get identity status string for display
 * @param {object} user - User object from context
 * @returns {string} - 'verified' | 'pending' | 'failed' | 'unverified'
 */
export function getIdentityStatusString(user) {
  if (!user) return 'unverified';
  
  const identity = user.identity || {};
  if (identity.status) return identity.status;
  
  // Legacy fallback
  if (user.identity_verification_status) return user.identity_verification_status;
  
  return 'unverified';
}
